// 20260419 -- Copilot (Claude Sonnet 4.6) -- Frontend MCP service (axios wrappers)

/**
 * McpService — Frontend axios wrappers for MCP management endpoints.
 *
 * CREATED: 20260419
 * WHO: Copilot (Claude Sonnet 4.6)
 * WHY: Centralizes all HTTP calls from the MCP settings UI (connections,
 *      audit log, undo) so components stay thin.
 *
 * All calls include the Auth0 bearer token. Endpoints hit /api/mcp/v1/* and
 * /api/v3/admin/mcp/* (admin only).
 *
 * USAGE:
 *   import McpService from '@/services/McpService'
 *   const { data } = await McpService.getAuditLog(token)
 */

import axios from 'axios'

// 20260419 -- Copilot (Claude Sonnet 4.6) -- Fix PR#100: use /api/v3/mcp for user-facing calls so the
// regular app JWT ($auth.getTokenSilently()) works; /api/mcp/v1 requires a separate MCP-audience token
const V3_MCP_BASE = '/api/v3/mcp'

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` }
}

class McpService {

  // ── Connections ──────────────────────────────────────────────────────────────

  /**
   * getConnections — List the user's active MCP connections.
   * Returns { successful, data: [connection, ...] }
   */
  static getConnections(token) {
    return axios.get(`${V3_MCP_BASE}/connections`, { headers: authHeaders(token) })
  }

  /**
   * revokeConnection — Revoke a specific connection.
   * @param {string} connectionId
   */
  static revokeConnection(connectionId, token) {
    return axios.post(
      `${V3_MCP_BASE}/connections/${connectionId}/revoke`,
      {},
      { headers: authHeaders(token) }
    )
  }

  /**
   * initiateOAuth — Returns the Auth0 PKCE authorization URL so the user can
   * connect a new MCP client. The MCP OAuth flow is client-driven (PKCE) and
   * uses Auth0 discovery (/.well-known/oauth-authorization-server). This method
   * is kept for backwards compatibility but the settings UI should redirect
   * the user to the Auth0 authorization URL directly using the discovery endpoint.
   *
   * @param {string} clientId   - The MCP client identifier (e.g. 'claude.ai')
   * @param {string} scopes     - Space-separated scope string
   * @param {string} redirectUri
   * @returns {Promise} resolves to { data: { authorization_url } }
   */
  static initiateOAuth(clientId, scopes, redirectUri) {
    // Build the Auth0 authorization URL client-side using the well-known discovery data.
    // The discovery endpoint (/.well-known/oauth-authorization-server) provides the
    // authorization_endpoint — redirect the user there with standard PKCE params.
    const domain    = window.__MCP_AUTH0_DOMAIN__ || ''
    const clientIdV = window.__MCP_CLIENT_ID__    || clientId
    const url = `https://${domain}/authorize?` + new URLSearchParams({
      response_type: 'code',
      client_id:     clientIdV,
      redirect_uri:  redirectUri,
      scope:         scopes,
      audience:      window.__MCP_AUDIENCE__ || '',
    }).toString()
    return Promise.resolve({ data: { authorization_url: url } })
  }

  // ── Audit log ────────────────────────────────────────────────────────────────

  /**
   * getAuditLog — Paginated audit log for the current user.
   * @param {string} token
   * @param {Object} params  - { cursor, limit, tool_name }
   * @returns {Promise} resolves to { data: { successful, data: { entries, next_cursor } } }
   */
  static getAuditLog(token, params = {}) {
    return axios.get(`${V3_MCP_BASE}/audit`, {
      headers: authHeaders(token),
      params,
    })
  }

  /**
   * undoEntry — Undo a single audit log entry.
   * @param {string} entryId
   */
  static undoEntry(entryId, token) {
    return axios.post(
      `${V3_MCP_BASE}/audit/${entryId}/undo`,
      {},
      { headers: authHeaders(token) }
    )
  }

  /**
   * undoRequest — Undo all entries belonging to a request (atomic).
   * @param {string} requestId
   */
  static undoRequest(requestId, token) {
    return axios.post(
      `${V3_MCP_BASE}/audit/request/${requestId}/undo`,
      {},
      { headers: authHeaders(token) }
    )
  }

  // ── Personal Access Tokens ───────────────────────────────────────────────────

  /**
   * generatePat — Generate a long-lived Personal Access Token (paid users only).
   * Returns { successful, data: { token, connection_id, expires_at } }
   * The token is shown exactly once — the caller must handle it immediately.
   *
   * @param {string} token           - Auth0 bearer token
   * @param {Object} params
   * @param {string} params.client_name
   * @param {string[]} params.scopes
   * @param {number} [params.expiration_days]  - 1–365, default 90
   */
  // 20260419 -- Copilot (Claude Sonnet 4.6) -- PAT generation for headless MCP clients
  static generatePat(token, { client_name, scopes, expiration_days }) {
    return axios.post(
      `${V3_MCP_BASE}/tokens`,
      { client_name, scopes, expiration_days },
      { headers: authHeaders(token) }
    )
  }

  // ── Admin (stg-admin role required) ─────────────────────────────────────────

  /**
   * adminGetAuditLog — Cross-user audit log for admins.
   * @param {string} token
   * @param {Object} params  - { userId, cursor, limit, tool }
   */
  static adminGetAuditLog(token, params = {}) {
    return axios.get('/api/v3/admin/mcp/audit', {
      headers: authHeaders(token),
      params,
    })
  }

  /**
   * adminGetConnections — Connection list for admins.
   * @param {string} token
   * @param {Object} params  - { userId }
   */
  static adminGetConnections(token, params = {}) {
    return axios.get('/api/v3/admin/mcp/connections', {
      headers: authHeaders(token),
      params,
    })
  }
}

export default McpService
